import React from 'react'
import { Card, CardContent, CardHeader } from '@/shared/ui/Card'
import { Button, Badge, Modal } from '@/shared/ui'
import { PLAN_INTERVAL_LABEL, type Plan } from '../types'
import { useMemberPlans } from '../hooks/useMemberPlans'
import { computePaidOk } from '@/modules/auth/utils/subscriptionGate'
import {
  previewChangeSubscriptionPlan,
  applyChangeSubscriptionPlan,
  getNextCycleInfo,
  cancelSubscriptionAtPeriodEnd,
  cancelScheduledChange,
  syncSubscriptionStatus,
} from '../services/memberPlanService'
import StripePayment from '@/modules/checkout/components/StripePayment'
import { getPlanDiscountPercent, getPlanDiscountRecurrence, getPriceAfterPlanDiscount } from '@/modules/plans/utils/pricing'
import { useToast } from '@/shared/ui/Toast'
import {
  clearPendingPlanChange,
  getPendingPlanChange,
  savePendingPlanChange,
  type PendingPlanChange,
} from '@/modules/plans/utils/pendingPlanChange'

type NextCycleState = {
  current_period_end: string | null
  scheduled_change?: { id: string; name: string } | null
  vip_discount_percent?: number
  vip_free?: boolean
  next_amount_due_cents?: number | null
  next_subtotal_cents?: number | null
  next_currency?: string | null
}

function formatCurrency(cents: number, currency: string = 'BRL') {
  const value = (cents || 0) / 100
  try {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency }).format(value)
  } catch {
    return `R$ ${value.toFixed(2)}`
  }
}

export default function MemberPlansPage() {
  const { loading, error, plans, current, subscription, reload } = useMemberPlans()
  const { add: toast } = useToast()
  const [upgradeClientSecret, setUpgradeClientSecret] = React.useState<string | null>(null)
  const [upgradePublishable, setUpgradePublishable] = React.useState<string | null>(null)
  const [nextCycle, setNextCycle] = React.useState<NextCycleState>({
    current_period_end: null,
    scheduled_change: null,
    vip_discount_percent: 0,
    vip_free: false,
    next_amount_due_cents: null,
    next_subtotal_cents: null,
    next_currency: null,
  })
  const [hint, setHint] = React.useState<{ id: string; name?: string; when?: string } | null>(() => {
    try {
      const raw = localStorage.getItem('pending_schedule_hint')
      return raw ? JSON.parse(raw) : null
    } catch {
      return null
    }
  })
  const [pendingChange, setPendingChange] = React.useState<PendingPlanChange | null>(() => getPendingPlanChange())
  const [pendingSyncFor, setPendingSyncFor] = React.useState<string | null>(null)

  const registerPendingChange = React.useCallback((subscriptionId: string, targetPlanId: string) => {
    const next = savePendingPlanChange({ subscriptionId, targetPlanId })
    if (next) {
      setPendingChange(next)
      setPendingSyncFor(null)
    }
  }, [])

  React.useEffect(() => {
    ;(async () => {
      try {
        const info = await getNextCycleInfo()
        setNextCycle(info as NextCycleState)
        if (info?.scheduled_change) {
          try {
            localStorage.removeItem('pending_schedule_hint')
          } catch {}
          setHint(null)
        }
      } catch {
        // mantém estado anterior em falha transitória
      }
    })()
  }, [])

  React.useEffect(() => {
    if (!pendingChange?.subscriptionId) return
    if (pendingChange.subscriptionId === pendingSyncFor) return
    let cancelled = false
    setPendingSyncFor(pendingChange.subscriptionId)
    ;(async () => {
      try {
        await syncSubscriptionStatus(pendingChange.subscriptionId)
        if (!cancelled) await reload()
        if (!cancelled && !pendingChange.targetPlanId) {
          clearPendingPlanChange()
          setPendingChange(null)
          setPendingSyncFor(null)
        }
      } catch {
        // silencioso: fallback de sync
      }
    })()
    return () => { cancelled = true }
  }, [pendingChange?.subscriptionId, pendingChange?.targetPlanId, pendingSyncFor, reload])

  React.useEffect(() => {
    if (!pendingChange?.subscriptionId || !pendingChange.targetPlanId || !current?.id) return
    if (current.id !== pendingChange.targetPlanId) return
    clearPendingPlanChange()
    setPendingChange(null)
    setPendingSyncFor(null)
  }, [current?.id, pendingChange?.subscriptionId, pendingChange?.targetPlanId])

  const sortedPlans = React.useMemo(() => {
    const others = plans.filter((p) => !current || p.id !== current.id)
    const sortByPrice = (a: Plan, b: Plan) => a.amount_cents - b.amount_cents
    return {
      current,
      upgrades: current ? others.filter((p) => p.amount_cents > current.amount_cents).sort(sortByPrice) : [],
      downgrades: current ? others.filter((p) => p.amount_cents <= current.amount_cents).sort(sortByPrice) : others.sort(sortByPrice),
    }
  }, [plans, current])

  const [confirmPlan, setConfirmPlan] = React.useState<Plan | null>(null)
  const [preview, setPreview] = React.useState<any>(null)
  const payRef = React.useRef<null | (() => Promise<void>)>(null)
  const [payStatus, setPayStatus] = React.useState<'idle' | 'processing' | 'paid' | 'error'>('idle')
  const [upgradeSubscriptionId, setUpgradeSubscriptionId] = React.useState<string | null>(null)
  const [pendingPlanId, setPendingPlanId] = React.useState<string | null>(null)
  const [pendingCancel, setPendingCancel] = React.useState(false)

  const vipPct = Math.max(0, Math.min(100, Number(nextCycle.vip_discount_percent || 0)))
  const isVipFree = !!nextCycle.vip_free || vipPct >= 100

  const currentPlanIdNorm = String(current?.id || '').trim().toLowerCase()
  const currentIsFreemium = currentPlanIdNorm === 'trial_7d'
  const currentPaidOk = computePaidOk(subscription)
  let currentAction: PlanCardProps['currentAction'] = 'none'
  if (current && !currentIsFreemium) currentAction = currentPaidOk ? 'cancel' : 'renew'

  async function onSelectPlan(plan: Plan) {
    try {
      setPendingPlanId(plan.id)
      if (current && plan.id === current.id) {
        toast({ title: 'Plano', description: 'Você já está neste plano.', soft: true })
        return
      }
      const targetRef = plan.id
      const res = await previewChangeSubscriptionPlan(targetRef, plan.name)
      if (res?.message === 'already_on_plan' && res?.subscription_id) {
        try {
          await syncSubscriptionStatus(res.subscription_id)
        } catch {}
        await reload()
        setNextCycle((await getNextCycleInfo()) as NextCycleState)
        toast({ title: 'Plano', description: 'Seu plano já está ativo. Sincronizamos seus dados.', variant: 'success', soft: true })
        return
      }
      if (res?.mode === 'no_subscription' || res?.message === 'no_subscription') {
        window.location.href = `/checkout?plan=${encodeURIComponent(targetRef)}&interval=${encodeURIComponent(plan.interval || 'month')}`
        return
      }
      setConfirmPlan(plan)
      setPreview(res)
    } catch (e: any) {
      toast({ title: 'Falha', description: e?.message || 'Não foi possível alterar o plano', variant: 'error', soft: true })
    } finally {
      setPendingPlanId(null)
    }
  }

  React.useEffect(() => {
    ;(async () => {
      try {
        if (confirmPlan && preview?.mode === 'upgrade' && !upgradeClientSecret) {
          const res = await applyChangeSubscriptionPlan(confirmPlan.id, confirmPlan.name)
          if (res.client_secret) {
            setUpgradeClientSecret(res.client_secret)
            setUpgradePublishable(res.publishable || null)
          }
          if (res.subscription_id) {
            setUpgradeSubscriptionId(res.subscription_id)
            registerPendingChange(res.subscription_id, confirmPlan.id)
          }
        }
      } catch {
        // silencioso; usuário pode tentar novamente
      }
    })()
  }, [confirmPlan?.id, preview?.mode])

  return (
    <div className="mx-auto w-full max-w-[1550px]">
      <div className="mb-6 text-center">
        <div className="text-xs uppercase tracking-wider text-foreground/70">Planos de Assinatura</div>
        <h1 className="mt-1 text-2xl font-semibold">Escolha o plano ideal para você</h1>
      </div>

      {error && (
        <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-foreground/70">Carregando...</div>
      ) : (
        <div className="space-y-8">
          {(nextCycle.current_period_end || nextCycle.scheduled_change) && !isVipFree && (
            <section className="rounded-md border border-border/60 bg-muted/30 p-3 text-sm text-foreground/80">
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <div>
                  {nextCycle.current_period_end && (
                    <div>
                      Próximo ciclo em{' '}
                      <span className="font-medium">
                        {new Date(nextCycle.current_period_end).toISOString().replace('T', ' ').slice(0, 19)} UTC
                      </span>
                      .
                    </div>
                  )}
                  {typeof nextCycle.next_amount_due_cents === 'number' && (
                    <div className="text-xs text-foreground/70">
                      Próxima cobrança: {formatCurrency(nextCycle.next_amount_due_cents, nextCycle.next_currency || 'BRL')} por ciclo.
                    </div>
                  )}
                  {nextCycle.scheduled_change && (
                    <div className="text-xs text-foreground/70">
                      Próximo plano agendado: <span className="font-medium">{nextCycle.scheduled_change.name}</span>.
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  {nextCycle.scheduled_change && (
                    <Button
                      variant="outline"
                      onClick={async () => {
                        try {
                          const ok = await cancelScheduledChange()
                          if (ok) {
                            toast({ title: 'Alteração cancelada', variant: 'info', soft: true })
                            setNextCycle((await getNextCycleInfo()) as NextCycleState)
                            try {
                              localStorage.removeItem('pending_schedule_hint')
                            } catch {}
                            setHint(null)
                          }
                        } catch (e: any) {
                          toast({
                            title: 'Falha',
                            description: e?.message || 'Não foi possível cancelar a alteração',
                            variant: 'error',
                            soft: true,
                          })
                        }
                      }}
                    >
                      Cancelar alteração
                    </Button>
                  )}
                </div>
              </div>
            </section>
          )}

          {sortedPlans.current && (
            <section>
              <div className="mb-3 text-sm font-medium">Seu plano atual</div>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                <PlanCard
                  plan={sortedPlans.current}
                  current
                  currentAction={currentAction}
                  busy={currentAction === 'cancel' ? pendingCancel : false}
                  vipFree={isVipFree}
                  vipPct={vipPct}
                  nextAmountDueCents={nextCycle.next_amount_due_cents ?? null}
                  onSelect={onSelectPlan}
                  onCancel={currentAction === 'cancel' ? async () => {
                    try {
                      setPendingCancel(true)
                      const ends = await cancelSubscriptionAtPeriodEnd()
                      toast({
                        title: 'Assinatura',
                        description: `Cancelada. Acesso até ${
                          ends ? new Date(ends).toLocaleDateString('pt-BR') : 'o fim do ciclo'
                        }.`,
                        variant: 'info',
                        soft: true,
                        durationMs: 7000,
                      })
                    } catch (e: any) {
                      toast({
                        title: 'Falha',
                        description: e?.message || 'Não foi possível cancelar o plano',
                        variant: 'error',
                        soft: true,
                      })
                    } finally {
                      setPendingCancel(false)
                    }
                  } : undefined}
                  onRenew={currentAction === 'renew' ? () => {
                    const planRef = sortedPlans.current?.id || null
                    if (!planRef) return
                    const interval = sortedPlans.current?.interval || 'month'
                    window.location.href = `/checkout?plan=${encodeURIComponent(planRef)}&interval=${encodeURIComponent(interval)}`
                  } : undefined}
                />
                {(() => {
                  const sc = nextCycle.scheduled_change || (hint ? { id: hint.id, name: hint.name || '' } : null)
                  if (!sc) return null
                  const sp = plans.find((p) => p.id === sc.id)
                  if (!sp) return null
                  const when = nextCycle.current_period_end || hint?.when || null
                  const note = when
                    ? `Este será seu próximo plano a partir de ${new Date(when).toISOString().replace('T', ' ').slice(0, 19)} UTC`
                    : 'Este será seu próximo plano no próximo ciclo'
                  return <PlanCard plan={sp} scheduled faded note={note} onSelect={() => {}} vipFree={isVipFree} vipPct={vipPct} />
                })()}
              </div>
            </section>
          )}

          {sortedPlans.upgrades.length > 0 && (
            <section>
              <div className="mb-3 text-sm font-medium">Upgrades disponíveis</div>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {sortedPlans.upgrades.map((p) => (
                  <PlanCard
                    key={p.id}
                    plan={p}
                    cta={pendingPlanId === p.id ? 'Carregando...' : 'Fazer upgrade'}
                    busy={pendingPlanId === p.id}
                    onSelect={onSelectPlan}
                    scheduled={nextCycle.scheduled_change?.id === p.id}
                    vipFree={isVipFree}
                    vipPct={vipPct}
                  />
                ))}
              </div>
            </section>
          )}

          {sortedPlans.downgrades.length > 0 && (
            <section>
              <div className="mb-3 text-sm font-medium">Outros planos</div>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {sortedPlans.downgrades.map((p) => (
                  <PlanCard
                    key={p.id}
                    plan={p}
                    cta={pendingPlanId === p.id ? 'Carregando...' : sortedPlans.current ? 'Fazer downgrade' : 'Assinar'}
                    busy={pendingPlanId === p.id}
                    onSelect={onSelectPlan}
                    scheduled={nextCycle.scheduled_change?.id === p.id}
                    vipFree={isVipFree}
                    vipPct={vipPct}
                  />
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      <ConfirmChangeModal
        open={!!confirmPlan}
        onClose={() => {
          setConfirmPlan(null)
          setPreview(null)
          setUpgradeClientSecret(null)
          setUpgradePublishable(null)
          setPayStatus('idle')
        }}
        plan={confirmPlan}
        preview={preview}
        clientSecret={upgradeClientSecret}
        publishable={upgradePublishable}
        payRef={payRef}
        payStatus={payStatus}
        onGenerate={async () => {
          if (!confirmPlan) return
          try {
            const res = await applyChangeSubscriptionPlan(confirmPlan.id, confirmPlan.name)
            if (res.client_secret) {
              setUpgradeClientSecret(res.client_secret)
              setUpgradePublishable(res.publishable || null)
            }
            if (res.subscription_id) {
              setUpgradeSubscriptionId(res.subscription_id)
              registerPendingChange(res.subscription_id, confirmPlan.id)
            }
          } catch (e: any) {
            toast({
              title: 'Falha',
              description: e?.message || 'Não foi possível iniciar o pagamento',
              variant: 'error',
              soft: true,
            })
          }
        }}
        onPay={async () => {
          if (!payRef.current) return
          setPayStatus('processing')
          await payRef.current()
        }}
        onPaid={async () => {
          setPayStatus('paid')
          toast({ title: 'Pagamento aprovado', variant: 'success', soft: true })
          if (upgradeSubscriptionId) {
            try {
              await syncSubscriptionStatus(upgradeSubscriptionId)
            } catch {}
          }
          if (preview?.mode === 'downgrade' && confirmPlan) {
            try {
              const res2 = await applyChangeSubscriptionPlan(confirmPlan.id, confirmPlan.name)
              if (res2.mode === 'downgrade') {
                const when = res2.scheduled_for
                  ? new Date(res2.scheduled_for).toISOString().replace('T', ' ').slice(0, 19) + ' UTC'
                  : 'próximo ciclo'
                toast({
                  title: 'Downgrade agendado',
                  description: `Seu plano será ajustado em ${when}.`,
                  variant: 'info',
                  soft: true,
                  durationMs: 6000,
                })
                try {
                  localStorage.setItem(
                    'pending_schedule_hint',
                    JSON.stringify({ id: confirmPlan.id, name: confirmPlan.name, when: res2.scheduled_for || null }),
                  )
                } catch {}
              }
            } catch {
              // silencioso
            }
          }
          await reload()
          setNextCycle((await getNextCycleInfo()) as NextCycleState)
          setConfirmPlan(null)
          setPreview(null)
          setUpgradeClientSecret(null)
          setUpgradePublishable(null)
          setUpgradeSubscriptionId(null)
        }}
        onPaymentError={(msg) => {
          setPayStatus('error')
          toast({
            title: 'Pagamento',
            description: msg || 'Falha ao processar o pagamento',
            variant: 'error',
            soft: true,
          })
        }}
        onConfirm={async () => {
          if (!confirmPlan) return
          try {
            const res = await applyChangeSubscriptionPlan(confirmPlan.id, confirmPlan.name)
            if (res.mode === 'downgrade') {
              const when = res.scheduled_for
                ? new Date(res.scheduled_for).toISOString().replace('T', ' ').slice(0, 19) + ' UTC'
                : 'próximo ciclo'
              toast({
                title: 'Downgrade agendado',
                description: `Seu plano será ajustado em ${when}.`,
                variant: 'info',
                soft: true,
                durationMs: 6000,
              })
              setConfirmPlan(null)
              setPreview(null)
              try {
                localStorage.setItem(
                  'pending_schedule_hint',
                  JSON.stringify({ id: confirmPlan.id, name: confirmPlan.name, when: res.scheduled_for || null }),
                )
              } catch {}
              setNextCycle((await getNextCycleInfo()) as NextCycleState)
            }
          } catch (e: any) {
            toast({
              title: 'Falha',
              description: e?.message || 'Não foi possível agendar o downgrade',
              variant: 'error',
              soft: true,
            })
          }
        }}
      />
    </div>
  )
}

type PlanCardProps = {
  plan: Plan
  current?: boolean
  currentAction?: 'cancel' | 'renew' | 'none'
  cta?: string
  onSelect: (plan: Plan) => void
  onCancel?: () => void
  onRenew?: () => void
  scheduled?: boolean
  faded?: boolean
  note?: string
  busy?: boolean
  vipFree?: boolean
  vipPct?: number
  nextAmountDueCents?: number | null
}

function PlanCard({
  plan,
  current = false,
  currentAction = 'none',
  cta,
  onSelect,
  onCancel,
  onRenew,
  scheduled = false,
  faded = false,
  note,
  busy = false,
  vipFree = false,
  vipPct = 0,
  nextAmountDueCents,
}: PlanCardProps) {
  const basePriceCents = plan.amount_cents
  const currency = (plan.currency || 'BRL').toUpperCase()
  const label = PLAN_INTERVAL_LABEL[plan.interval] || plan.interval
  const vipPercent = Math.max(0, Math.min(100, Number(vipPct || 0)))
  const isFree = !!vipFree

  let effectiveCents: number
  if (current && typeof nextAmountDueCents === 'number' && nextAmountDueCents >= 0) {
    effectiveCents = nextAmountDueCents
  } else {
    const afterPlan = getPriceAfterPlanDiscount(plan)
    if (isFree) effectiveCents = 0
    else if (vipPercent > 0 && vipPercent < 100) {
      effectiveCents = Math.max(0, Math.floor(afterPlan * (100 - vipPercent) / 100))
    } else {
      effectiveCents = afterPlan
    }
  }

  const hasPlanDiscount = getPlanDiscountPercent(plan) > 0

  return (
    <Card className={`flex flex-col justify-between rounded-xl border border-border/60 bg-card p-0 shadow-lg ${faded ? 'opacity-60' : ''}`}>
      <CardHeader className="flex items-center justify-between">
        <div className="text-lg font-semibold">{plan.name}</div>
        <div className="flex items-center gap-2">
          {scheduled && <Badge variant="soft" color="warning">Proximo</Badge>}
          {current && <Badge variant="soft" color="success">Atual</Badge>}
          {isFree && <Badge variant="soft" color="success">VIP 100%</Badge>}
          {!isFree && vipPercent > 0 && <Badge variant="soft" color="info">-{vipPercent.toFixed(0)}% VIP</Badge>}
        </div>
      </CardHeader>
      <CardContent>
        <div className="mb-2 text-3xl font-bold">{isFree ? 'Gratuito' : formatCurrency(effectiveCents, currency)}</div>
        {!isFree && current && hasPlanDiscount && (
          <div className="mb-1 text-xs text-foreground/60">
            Preco cheio: {formatCurrency(basePriceCents, currency)}
          </div>
        )}
        {note && <div className="mb-3 text-xs text-foreground/60">{note}</div>}
        <div className="mb-4 text-sm text-foreground/70">
          {isFree ? 'VIP 100% (sem cobranca)' : `Cobranca ${label.toLowerCase()}`}
        </div>
        {plan.description && <p className="mb-4 text-sm text-foreground/80">{plan.description}</p>}
        {plan.features?.length > 0 && (
          <ul className="mb-6 list-disc space-y-1 pl-5 text-sm text-foreground/80">
            {plan.features.slice(0, 5).map((f, i) => (
              <li key={i}>{f}</li>
            ))}
          </ul>
        )}
        <div className="pt-2">
          {current ? (
            currentAction === 'renew' ? (
              <Button
                onClick={() => onRenew && onRenew()}
                className="w-full bg-lime-500 text-black hover:bg-lime-400 focus-visible:ring-lime-500"
                disabled={busy}
              >
                {busy ? 'Processando...' : 'Renovar plano'}
              </Button>
            ) : currentAction === 'cancel' ? (
              <Button variant="outline" onClick={() => onCancel && onCancel()} className="w-full" disabled={busy}>
                {busy ? 'Processando...' : 'Cancelar plano'}
              </Button>
            ) : null
          ) : (
            <Button onClick={() => onSelect(plan)} className="w-full" disabled={busy}>
              {cta || 'Assinar'}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

function UpgradePaymentModal({ clientSecret, publishable, onClose }: { clientSecret: string | null; publishable: string | null; onClose: () => void }) {
  if (!clientSecret || !publishable) return null
  return (
    <Modal open={!!clientSecret} onOpenChange={(v) => { if (!v) onClose() }}>
      <div className="space-y-3">
        <div className="text-lg font-semibold">Completar pagamento de upgrade</div>
        <StripePayment publishableKey={publishable} clientSecret={clientSecret} onPaid={onClose} onError={() => {}} />
        <div className="flex justify-end">
          <Button variant="outline" onClick={onClose}>
            Fechar
          </Button>
        </div>
      </div>
    </Modal>
  )
}

function ConfirmChangeModal({
  open,
  onClose,
  plan,
  preview,
  clientSecret,
  publishable,
  onGenerate,
  onPay,
  onPaid,
  onPaymentError,
  payRef,
  payStatus,
  onConfirm,
}: {
  open: boolean
  onClose: () => void
  plan: Plan | null
  preview: any
  clientSecret: string | null
  publishable: string | null
  onGenerate: () => void
  onPay: () => void
  onPaid: () => void
  onPaymentError: (msg?: string) => void
  payRef: React.MutableRefObject<null | (() => Promise<void>)>
  payStatus: 'idle' | 'processing' | 'paid' | 'error'
  onConfirm?: () => void
}) {
  const amountCurrency = (plan?.currency || 'BRL').toUpperCase()
  const immediate = ((preview?.preview?.amount_due_cents || 0) / 100)
  return (
    <Modal open={open} onOpenChange={(v) => { if (!v) onClose() }}>
      <div className="space-y-3">
        <div className="text-lg font-semibold">Confirmar alteração de plano</div>
        <div className={`grid grid-cols-1 gap-6 ${preview?.mode === 'upgrade' ? 'md:grid-cols-2' : ''}`}>
          <div className="text-sm text-foreground/80 space-y-2">
            {plan && (
              <>
                <div>
                  Plano de destino: <span className="font-medium">{plan.name}</span>
                </div>
                {preview?.mode === 'upgrade' && preview?.preview && (
                  <div>
                    Valor imediato (pró-rata):{' '}
                    <span className="font-medium">
                      {immediate.toLocaleString('pt-BR', { style: 'currency', currency: amountCurrency })}
                    </span>
                    <div className="text-foreground/60">
                      Ciclo atual permanece até{' '}
                      {preview.preview.period_end
                        ? new Date(preview.preview.period_end).toLocaleDateString('pt-BR')
                        : 'o fim do ciclo'}
                      .
                    </div>
                  </div>
                )}
                {preview?.mode === 'downgrade' && (
                  <div>
                    A alteração será aplicada no próximo ciclo em{' '}
                    <span className="font-medium">
                      {preview.scheduled_for
                        ? new Date(preview.scheduled_for).toLocaleDateString('pt-BR')
                        : 'data do próximo ciclo'}
                    </span>
                    .
                  </div>
                )}
              </>
            )}
          </div>

          {(preview?.mode === 'upgrade' || (clientSecret && publishable)) && (
            <div>
              {clientSecret && publishable ? (
                <div className="space-y-3">
                  <div className="text-sm text-foreground/70">Dados do cartão</div>
                  <StripePayment
                    publishableKey={publishable}
                    clientSecret={clientSecret}
                    onPaid={onPaid}
                    onError={(m) => onPaymentError(m)}
                    controlRef={payRef}
                  />
                </div>
              ) : (
                <div className="rounded-md border border-border/60 bg-muted/30 p-3 text-sm text-foreground/70">
                  Gerando cobrança.
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>
            Fechar
          </Button>
          {(preview?.mode === 'upgrade' || (clientSecret && publishable)) && (
            clientSecret && publishable ? (
              <Button onClick={onPay} disabled={payStatus === 'processing'}>
                {payStatus === 'processing' ? 'Processando...' : 'Pagar agora'}
              </Button>
            ) : (
              <Button onClick={onGenerate}>Gerar cobrança</Button>
            )
          )}
          {preview?.mode === 'downgrade' && (
            <Button onClick={onConfirm}>Confirmar</Button>
          )}
        </div>
      </div>
    </Modal>
  )
}
